import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth"; // Assuming useAuth provides token and user
import { logger } from "@/utils/logger";
import { WebSocketContext, WebSocketMessage } from "@/contexts/WebSocketTypes";

interface WebSocketProviderProps {
  children: React.ReactNode;
  wsUrlBase: string; // e.g., wss://yourdomain.com/api or ws://localhost:8000/api
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  wsUrlBase,
}) => {
  const { token, isAuthenticated, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<MessageEvent | null>(null);
  const [error, setError] = useState<Event | null>(null);
  const webSocketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY_MS = 5000; // 5 seconds

  const connect = useCallback(() => {
    if (!isAuthenticated || !token || !user) {
      logger.info("WebSocket: Not authenticated, connection not attempted.");
      if (
        webSocketRef.current &&
        webSocketRef.current.readyState !== WebSocket.CLOSED
      ) {
        logger.info("WebSocket: Closing existing connection due to auth loss.");
        webSocketRef.current.close();
      }
      return;
    }

    if (
      webSocketRef.current &&
      (webSocketRef.current.readyState === WebSocket.OPEN ||
        webSocketRef.current.readyState === WebSocket.CONNECTING)
    ) {
      logger.info("WebSocket: Connection already open or connecting.");
      return;
    }

    const fullWsUrl = `${wsUrlBase}/ws/general?token=${encodeURIComponent(token)}`;
    logger.info(`WebSocket: Attempting to connect to ${fullWsUrl}`);

    try {
      const ws = new WebSocket(fullWsUrl);
      webSocketRef.current = ws;

      ws.onopen = () => {
        logger.info("WebSocket: Connection established.");
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        try {
          // Parse the message data
          const messageData = JSON.parse(event.data) as WebSocketMessage;
          switch (messageData.type) {
            case "generation_progress":
              // Log all status messages consistently
              logger.debug(
                `WebSocket: Generation progress message: ${JSON.stringify(messageData)}`,
              );
              break;

            case "translation_progress":
              // Log translation progress messages
              logger.debug(
                `WebSocket: Translation progress message: ${JSON.stringify(messageData)}`,
              );
              break;

            case "xp_award":
              // XP Award detailed logging
              logger.info(
                `XP Award received: +${messageData.xp_earned} XP - ${messageData.activity_description}`,
              );

              if (messageData.is_welcome_bonus) {
                logger.info("Welcome bonus received!");
              }

              if (messageData.level_up) {
                logger.info(
                  `Level up! ${messageData.old_level} → ${messageData.new_level}`,
                );
              }

              // Additional debugging info
              logger.debug("Full XP award message data:", messageData);
              break;

            case "pipeline_progress":
              // Pipeline progress detailed logging
              logger.info(
                `Pipeline Progress: ${messageData.step} - ${messageData.status}: ${messageData.message}`,
              );

              if (messageData.progress) {
                logger.debug(`Pipeline Progress: ${messageData.progress}%`);
              }

              if (messageData.error) {
                logger.error(`Pipeline Error: ${messageData.error}`);
              }

              // Additional debugging info
              logger.debug("Full pipeline progress message data:", messageData);
              break;

            default:
              // Log other/unknown message types
              logger.debug(
                `WebSocket: Message received: ${JSON.stringify(messageData)}`,
              );
              logger.warn(
                `WebSocket: Received unknown message type: ${(messageData as { type?: string }).type || "unknown"}`,
              );
              break;
          }

          // Set the last message for components to access
          setLastMessage(event);
        } catch (error) {
          logger.error(
            `WebSocket: Error parsing message data: ${error}`,
            event.data,
          );
        }
      };

      ws.onerror = (event) => {
        logger.error("WebSocket: Error occurred.", event);
        setError(event);
        // Note: onclose will usually follow an onerror
      };

      ws.onclose = (event) => {
        logger.info(
          `WebSocket: Connection closed. Code: ${event.code}, Reason: ${event.reason}, Was Clean: ${event.wasClean}`,
        );
        setIsConnected(false);
        webSocketRef.current = null; // Clear the ref

        // Handle specific close codes (e.g., auth failure)
        if (event.code === 4001 || event.code === 4003) {
          // Custom codes from backend for auth issues
          logger.warn(
            `WebSocket: Connection closed due to authentication failure (code: ${event.code}). Won't auto-reconnect.`,
          );
          setError(
            new ErrorEvent("AuthError", {
              message: `Connection closed by server: ${event.reason}`,
            }),
          );
          // Potentially trigger logout or token refresh here via AuthContext
          return; // Do not attempt to reconnect on auth errors
        }

        // Implement robust reconnect logic if not an auth error and connection was not cleanly closed by client
        if (
          !event.wasClean &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttemptsRef.current++;
          logger.info(
            `WebSocket: Attempting reconnect ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS} in ${RECONNECT_DELAY_MS / 1000}s`,
          );
          setTimeout(connect, RECONNECT_DELAY_MS);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          logger.warn("WebSocket: Max reconnect attempts reached.");
          setError(
            new ErrorEvent("ReconnectError", {
              message: "Max reconnect attempts reached.",
            }),
          );
        }
      };
    } catch (e) {
      logger.error("WebSocket: Failed to create WebSocket instance.", e);
      setError(
        new ErrorEvent("CreationError", {
          message: "Failed to create WebSocket",
        }),
      );
    }
  }, [token, isAuthenticated, user, wsUrlBase]);

  const disconnect = useCallback(() => {
    if (webSocketRef.current) {
      logger.info("WebSocket: Manual disconnect initiated.");
      webSocketRef.current.close(1000, "User initiated disconnect"); // 1000 is normal closure
      // onclose handler will set isConnected to false and clear webSocketRef
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token && user) {
      connect(); // Attempt to connect when user is authenticated
    } else {
      disconnect(); // Disconnect if user logs out or token becomes invalid
    }

    // Cleanup on component unmount
    return () => {
      if (webSocketRef.current) {
        logger.info("WebSocket: Provider unmounting, closing connection.");
        webSocketRef.current.close(1000, "Component unmounting");
      }
    };
  }, [isAuthenticated, token, user, connect, disconnect]);

  const sendMessage = (message: string) => {
    if (
      webSocketRef.current &&
      webSocketRef.current.readyState === WebSocket.OPEN
    ) {
      webSocketRef.current.send(message);
      logger.debug("WebSocket: Message sent:", message);
    } else {
      logger.warn("WebSocket: Connection not open. Message not sent:", message);
      // Optionally queue message or throw error
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        lastMessage,
        sendMessage,
        error,
        connect,
        disconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
